import { html } from 'lit';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import './pathFlowElement.js';

export interface PathJourneyItem {
  iconClass: string;
  title: string;
  text: string;
  image?: string;
  imageAlt?: string;
  diagram?: string[];
}

export interface PathJourneyElementIds {
  card: (index: number) => string;
  icon: (index: number) => string;
  title: (index: number) => string;
  text: (index: number) => string;
  image?: (index: number) => string;
}

interface PathJourneyOptions {
  items: PathJourneyItem[];
  ids: PathJourneyElementIds;
  editor?: LandingEditorRenderOptions;
  containerId?: string;
}

// Hand-drawn serpentine that weaves top-to-bottom between the two card columns. It is purely
// decorative (preserveAspectRatio="none" stretches it over the grid), which gives the playful,
// slightly random feel. pathLength="1" lets the scroll-draw animation run without the real length.
const serpentinePath =
  'M50 1 C 16 7, 14 19, 50 24 C 86 29, 88 41, 50 46 C 14 51, 12 63, 50 68 C 86 73, 88 85, 50 90 C 30 93, 44 97, 50 99';

export const pathJourney = ({ items, ids, editor, containerId }: PathJourneyOptions) => {
  const lastIndex = items.length - 1;

  return html`
    <vp-path class="vp-path" ?data-editor-static=${editor?.mode === 'editor'}>
      <svg class="vp-path-track" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <path class="vp-path-line vp-path-line-ghost" pathLength="1" d=${serpentinePath} />
        <path class="vp-path-line vp-path-line-draw" pathLength="1" d=${serpentinePath} />
        <circle class="vp-path-comet" r="7" cx="-100" cy="-100" />
      </svg>
      <ol
        class=${containerId ? editorNodeClass(editor, containerId, 'vp-path-grid') : 'vp-path-grid'}
        style=${containerId ? editorNodeStyle(editor, containerId) : ''}
        @pointerdown=${(event: PointerEvent) => containerId && editorNodePointerDown(editor, containerId, event)}
        @click=${(event: MouseEvent) => containerId && editorNodeClick(editor, containerId, event)}
      >
        ${items.map((item, index) => {
          const isLast = index === lastIndex;
          const cardId = ids.card(index);
          const iconId = ids.icon(index);
          const titleId = ids.title(index);
          const textId = ids.text(index);
          const imageId = ids.image?.(index);
          return html`
            <li
              class=${editorNodeClass(editor, cardId, `vp-path-card ${isLast ? 'is-goal' : ''}`)}
              style=${`--i: ${index}; ${editorNodeStyle(editor, cardId)}`}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, cardId, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, cardId, event)}
            >
              <div class="vp-path-card-head">
                <span class="vp-path-node">
                  <span
                    class=${editorNodeClass(editor, iconId, item.iconClass)}
                    style=${editorNodeStyle(editor, iconId)}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, iconId, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, iconId, event)}
                  ></span>
                  <span class="vp-path-node-index">${index + 1}</span>
                </span>
                <h3
                  class=${editorNodeClass(editor, titleId, 'vp-path-card-title')}
                  style=${editorNodeStyle(editor, titleId)}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, titleId, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, titleId, event)}
                >
                  ${item.title}
                </h3>
              </div>
              ${item.image
                ? html`<figure class="vp-path-media">
                    <img
                      class=${imageId ? editorNodeClass(editor, imageId) : ''}
                      style=${imageId ? editorNodeStyle(editor, imageId) : ''}
                      src=${item.image}
                      alt=${item.imageAlt ?? ''}
                      loading=${editor?.mode === 'editor' ? 'eager' : 'lazy'}
                      @pointerdown=${(event: PointerEvent) =>
                        imageId && editorNodePointerDown(editor, imageId, event)}
                      @click=${(event: MouseEvent) => imageId && editorNodeClick(editor, imageId, event)}
                    />
                  </figure>`
                : null}
              <p
                class=${editorNodeClass(editor, textId)}
                style=${editorNodeStyle(editor, textId)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, textId, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, textId, event)}
              >
                ${item.text}
              </p>
              ${item.diagram?.length
                ? html`
                    <div class="vp-path-diagram" aria-label=${item.diagram.join(', ')}>
                      ${item.diagram.map(
                        (label, diagramIndex) => html`
                          ${diagramIndex > 0
                            ? html`<span class="vp-path-diagram-arrow icon-[material-symbols--arrow-forward-rounded]" aria-hidden="true"></span>`
                            : ''}
                          <span class="vp-path-diagram-node">${label}</span>
                        `,
                      )}
                    </div>
                  `
                : ''}
            </li>
          `;
        })}
      </ol>
    </vp-path>
  `;
};
